import { useSortable, defaultAnimateLayoutChanges } from '@dnd-kit/sortable'
import { GripVerticalIcon, Trash2Icon } from 'lucide-react'

import { registerCountryCodeOptions } from '../../config'
import { useFormPlaygroundStore } from '../../stores/formPlaygroundStore'
import BubbleMenuEditor from '../shared/BubbleMenuEditor'
import { DatePicker } from '../shared/DatePicker'
import { DateRangePicker } from '../shared/DateRangePicker'
import RichTextEditor from '../shared/RichTextEditor'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Combobox } from '../ui/Combobox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Separator } from '../ui/separator'
import { Switch } from '../ui/switch'
import { Textarea } from '../ui/textarea'
import { Tooltip } from '../ui/tooltip'

import Options from './Options'

const animateLayoutChanges = (args) => {
  const { isSorting, wasDragging } = args
  if (isSorting || wasDragging) return defaultAnimateLayoutChanges(args)
  return true
}

function FormElementCard({ formElement, isView = false, field, isPreview = false }) {
  const { id, label, type, required, options } = formElement

  const removeFormElement = useFormPlaygroundStore((state) => state.removeFormElement)

  const toggleRequired = useFormPlaygroundStore((state) => state.toggleRequired)
  const updateLabel = useFormPlaygroundStore((state) => state.updateLabel)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    animateLayoutChanges,
  })

  const cardStyle = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  }

  return (
    <article
      className={`relative flex gap-2 rounded-md py-2 ${
        isDragging ? 'z-10' : ''
      } ${isView ? 'px-0' : 'pl-2 pr-4 sm:pr-2'}`}
      ref={setNodeRef}
      style={cardStyle}
    >
      {isView ? null : (
        <div
          className={`flex cursor-move items-center rounded px-2 ${
            isDragging ? 'bg-muted' : 'hover:bg-muted'
          }`}
          {...listeners}
          {...attributes}
        >
          <GripVerticalIcon className="h-7 w-7 text-muted-foreground transition-colors duration-200" />
        </div>
      )}
      <div
        className={`flex-grow text-[15px]
          ${['heading'].includes(type) && 'space-y-1 font-semibold text-xl sm:text-2xl'}
          ${['description'].includes(type) && 'mt-1 text-base sm:text-lg text-gray-600'}
          ${isPreview ? 'pointer-events-none' : ''}`}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-8">
          <div
            className={`
              w-full
              ${['heading', 'description'].includes(type) ? 'space-y-2' : 'flex items-center gap-3 sm:gap-5'}
            `}
          >
            {type === 'switch' ? (
              <Switch checked={field?.value} onCheckedChange={field?.onChange} />
            ) : type === 'checkbox' ? (
              <Checkbox checked={field?.value} onCheckedChange={field?.onChange} />
            ) : null}

            <BubbleMenuEditor
              placeholder={['heading', 'description'].includes(type) ? label : 'Question or Text'}
              content={label}
              updateHandler={(html) => {
                updateLabel(id, html)
              }}
              readOnly={isView}
              className={`
                !min-h-0 w-full
                ${type === 'heading' && 'text-xl font-bold text-gray-900 sm:text-2xl'}
                ${type === 'description' && 'text-base text-gray-600 sm:text-lg'}
                ${isView && type === 'heading' && 'border-b-2 border-gray-200 pb-2 mb-3 sm:pb-3 sm:mb-4'}
                ${isView && type === 'description' && 'italic bg-gray-50 px-3 py-1 rounded-lg sm:px-4 sm:py-2'}
              `}
              style={{
                ...(type === 'heading' && { lineHeight: 1.3 }),
                ...(type === 'description' && { lineHeight: 1.6 }),
              }}
            />

            {isView && type === 'heading' && <div className="mb-2 sm:mb-4" />}
          </div>

          {!isView && (
            <div className="flex items-center self-end sm:self-auto mt-2 sm:mt-0">
              {['heading', 'description', 'switch', 'checkbox'].includes(type) ? null : (
                <div className="flex items-center gap-2">
                  <Switch
                    id={'required-' + id}
                    checked={required}
                    onCheckedChange={() => toggleRequired(id)}
                  />
                  <Label
                    className="cursor-pointer font-normal text-sm sm:text-base"
                    htmlFor={'required-' + id}
                  >
                    Required
                  </Label>
                </div>
              )}
              <Separator orientation="vertical" className="mx-2 sm:mx-4 h-6 sm:h-7" />
              <Tooltip asChild title="Delete">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="rounded-full hover:bg-destructive/5 h-8 w-8 sm:h-auto sm:w-auto"
                  onClick={() => {
                    removeFormElement(id)
                  }}
                >
                  <Trash2Icon className="h-4 w-4 sm:h-5 sm:w-5 text-destructive" />
                </Button>
              </Tooltip>
            </div>
          )}
        </div>

        {type === 'single-line' ? (
          <Input
            placeholder="Single line text"
            required={field ? required : false}
            value={field?.value ?? ''}
            onChange={field?.onChange}
            className="border bg-white border-input-border-color px-3 py-3 sm:py-6 rounded-sm text-sm"
          />
        ) : type === 'name' ? (
          <Input
            placeholder="Name"
            required={field ? required : false}
            value={field?.value ?? ''}
            onChange={field?.onChange}
            className="border bg-white border-input-border-color px-3 py-3 sm:py-6 rounded-sm text-sm"
          />
        ) : type === 'number' ? (
          <Input
            type="number"
            placeholder="Number"
            required={field ? required : false}
            value={field?.value ?? ''}
            onChange={field?.onChange}
            className="border bg-white border-input-border-color px-3 py-3 sm:py-6 rounded-sm text-sm"
          />
        ) : type === 'mobile-number' ? (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="flex   sm:flex-row gap-2 w-full ">
                <Combobox
                  className="w-2/5 md:w-1/5 lg:h-full border bg-white border-input-border-color"
                  contentClassName="max-h-60 overflow-y-auto"
                  options={registerCountryCodeOptions.map(({ code, label }) => ({
                    value: code,
                    label: label,
                  }))}
                  field={{
                    value: field?.value?.countryCode || 'AE +971',
                    onChange: (value) => {
                      const selectedCountry = registerCountryCodeOptions.find(
                        (opt) => opt.code === value
                      )
                      const phoneNumber = (field?.value?.number || '').slice(
                        0,
                        selectedCountry?.phoneLength
                      )

                      field?.onChange({
                        countryCode: value,
                        number: phoneNumber,
                        formatted: `${value} ${phoneNumber}`,
                      })
                    },
                  }}
                  placeholder="Code"
                  searchKey="label"
                />

                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Mobile Number"
                  required={field ? required : false}
                  value={field?.value?.number || ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/\D/g, '')
                    const countryCode = field?.value?.countryCode || 'AE +971'
                    const selectedCountry = registerCountryCodeOptions.find(
                      (opt) => opt.code === countryCode
                    )
                    const maxLength = selectedCountry?.phoneLength || 15
                    const number = rawValue.slice(0, maxLength)

                    field?.onChange({
                      countryCode,
                      number,
                      formatted: `${countryCode} ${number}`,
                    })
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  className="border bg-white border-input-border-color w-3/5 md:w-full px-3 py-3 sm:py-6 rounded-sm  appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] text-sm"
                />
              </div>
            </div>

            {field?.value?.number &&
              (() => {
                const selectedCountry = registerCountryCodeOptions.find(
                  (opt) => opt.code === field.value.countryCode
                )

                return (
                  selectedCountry?.phoneLength &&
                  field.value.number.length !== selectedCountry.phoneLength && (
                    <span className="text-xs sm:text-sm text-destructive">
                      Phone number must be {selectedCountry.phoneLength} digits
                    </span>
                  )
                )
              })()}
          </div>
        ) : type === 'email' ? (
          <Input
            type="email"
            placeholder="Email"
            required={field ? required : false}
            value={field?.value ?? ''}
            onChange={field?.onChange}
            className="border bg-white border-input-border-color px-3 py-3 sm:py-6 rounded-sm text-sm"
          />
        ) : type === 'multi-line' ? (
          <Textarea
            placeholder="Multi line text..."
            required={field ? required : false}
            value={field?.value ?? ''}
            onChange={field?.onChange}
            className=" px-3 py-3 sm:py-6 rounded-sm bg-white text-sm"
          />
        ) : type === 'rich-text' ? (
          <RichTextEditor field={field} />
        ) : ['checklist', 'multi-choice', 'dropdown', 'combobox'].includes(type) && !isView ? (
          <Options type={type} id={id} />
        ) : type === 'checklist' ? (
          <ul className="space-y-2 sm:space-y-3 border bg-white border-input-border-color px-3 py-4 sm:px-3 sm:py-6 rounded-sm">
            {options?.map(({ label, value }) => (
              <li key={value} className="flex items-center gap-2 sm:gap-3 text-sm">
                <Checkbox
                  id={value}
                  checked={field?.value?.includes(label) ?? false}
                  onCheckedChange={(checked) => {
                    if (checked) field?.onChange([...field.value, label])
                    else field?.onChange(field.value.filter((val) => val !== label))
                  }}
                />
                <Label htmlFor={value} className="flex h-5 items-center font-normal text-sm">
                  {label}
                </Label>
              </li>
            ))}
          </ul>
        ) : type === 'multi-choice' ? (
          <RadioGroup
            className="gap-2 sm:gap-3 border bg-white border-input-border-color px-3 py-4 sm:px-3 sm:py-6 rounded-sm"
            value={field?.value}
            onValueChange={field?.onChange}
          >
            {options?.map(({ label, value }) => (
              <div key={value} className="flex items-center space-x-2 sm:space-x-3 text-sm">
                <RadioGroupItem value={value} id={value} className=" border-black" />
                <Label htmlFor={value} className="flex h-5 items-center font-normal text-sm">
                  {label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        ) : type === 'dropdown' ? (
          <Select
            value={field?.value}
            onValueChange={field?.onChange}
            required={field ? required : false}
          >
            <SelectTrigger className="border bg-white border-input-border-color h-[40px] sm:h-[50px] px-3 py-2 sm:py-6 w-full rounded-sm text-sm">
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
            <SelectContent>
              {options?.map(({ label, value }) => (
                <SelectItem value={value} key={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : type === 'combobox' && options ? (
          <Combobox
            className="border bg-white border-input-border-color h-[40px] sm:h-[50px] px-3 py-2 sm:py-6 w-full rounded-sm text-sm"
            options={options}
            field={field}
          />
        ) : type === 'date' ? (
          <DatePicker field={field} />
        ) : type === 'date-range' ? (
          <DateRangePicker field={field} />
        ) : type === 'time' ? (
          <Input
            className="border h-[40px] sm:h-[50px] bg-white border-input-border-color px-3 py-2 sm:py-3 rounded-sm w-full text-muted-foreground text-sm"
            type="time"
            required={field ? required : false}
            value={field?.value ?? ''}
            onChange={field?.onChange}
          />
        ) : type === 'attachments' ? (
          <Input
            type="file"
            multiple
            required={field ? required : false}
            onChange={(e) => {
              const files = Array.from(e.target.files || [])
              field?.onChange(files)
            }}
            className="pt-1 sm:pt-1.5 text-muted-foreground border h-[40px] sm:h-[50px] bg-white border-input-border-color px-3 py-2 sm:py-4 rounded-sm text-sm"
          />
        ) : type === 'image' ? (
          <Input
            type="file"
            accept="image/*"
            className="pt-1 sm:pt-1.5 text-muted-foreground border h-[40px] sm:h-[50px] bg-white border-input-border-color px-3 py-2 sm:py-4 rounded-sm text-sm"
            required={field ? required : false}
            onChange={field?.onChange}
          />
        ) : null}
        {isView && required ? (
          <div className="pt-1 text-xs sm:text-sm text-destructive">* Required</div>
        ) : null}
      </div>
    </article>
  )
}

export default FormElementCard
